import type { Deck, DeckState, Navigation, ComponentState } from "./types";
const clamp = (n: number, max: number) => Math.max(0, Math.min(max, Number.isFinite(n) ? Math.trunc(n) : 0));

/** Reconstruct a deck prefix, never an unbounded click log.
 * Backtracking removes later references; a jump includes earlier deck passages.
 * Reset changes only the current slide's steps. Reload restores the URL position.
 */
export function stateAt(deck: Deck, slideIndex = 0, stepIndex = 0): DeckState {
  slideIndex = clamp(slideIndex, Math.max(0, deck.slides.length - 1));
  const slide = deck.slides[slideIndex];
  stepIndex = clamp(stepIndex, slide?.steps.length ?? 0);
  const state: DeckState = { slideIndex, stepIndex, passageHistory: [],
    activePassageThreads: Object.create(null), componentState: Object.create(null) };
  for (const [i, item] of deck.slides.entries()) {
    if (i > slideIndex) break;
    if (!item.passage) continue;
    if (!state.passageHistory.some(p => p.reference === item.passage))
      state.passageHistory.push({ reference: item.passage, slideId: item.id, slideIndex: i });
    if (item.thread) {
      const old = state.activePassageThreads[item.thread];
      const completed = old ? [...old.completed, old.current].filter((p, j, a) => p !== item.passage && a.indexOf(p) === j) : [];
      const known = deck.slides.find(s => s.thread === item.thread && s.threadPassages?.length)?.threadPassages ?? [];
      state.activePassageThreads[item.thread] = { label: item.threadLabel || item.thread,
        completed, current: item.passage, future: known.filter(p => p !== item.passage && !completed.includes(p)) };
    }
  }
  if (!slide) return state;
  const target = (id: string): ComponentState => state.componentState[id] ??= { visible: true };
  // A reveal anywhere starts hidden. Replaying actions gives exact inverse semantics.
  for (const step of slide.steps) for (const action of step.actions) {
    if (action.type === "reveal") Object.assign(target(action.target), { visible: false, effect: action.effect ?? "appear" });
    if (action.type === "visit") target(action.target).stopIndex = -1;
  }
  for (const step of slide.steps.slice(0, stepIndex)) for (const action of step.actions) {
    const value = target(action.target);
    switch (action.type) {
      case "reveal": value.visible = true; value.effect = action.effect ?? "appear"; break;
      case "hide": value.visible = false; break;
      case "visit": value.stopIndex = action.index; break;
      case "callout": value.callout = action.text; break;
      default: value.tone = action.type; break;
    }
  }
  return state;
}
export function navigate(deck: Deck, state: DeckState, command: Navigation): DeckState {
  let { slideIndex: i, stepIndex: s } = state;
  const count = deck.slides[i]?.steps.length ?? 0;
  if (typeof command === "object") return stateAt(deck, command.slideIndex, command.stepIndex ?? 0);
  switch (command) {
    case "next": if (s < count) s++; else if (i < deck.slides.length - 1) { i++; s = 0; } break;
    case "previous": if (s > 0) s--; else if (i > 0) { i--; s = deck.slides[i].steps.length; } break;
    case "next-slide": i++; s = 0; break;
    case "previous-slide": i--; s = 0; break;
    case "home": i = 0; s = 0; break;
    case "end": i = deck.slides.length - 1; s = deck.slides[i]?.steps.length ?? 0; break;
    case "reset": s = 0; break;
  }
  return stateAt(deck, i, s);
}
